import { motion } from "framer-motion";
import { Play, ExternalLink, Music2, Hash, Eye, Instagram } from "lucide-react";

/**
 * Reel Inspiration Panel
 * 3 cards per spot, each opens an Instagram Reels search for a different angle.
 * Each card now uses a DIFFERENT image (spot photo + 2 themed alternatives) and a
 * REAL popular hashtag so the IG page actually has content, not a login wall.
 */

// Themed pool of cinematic reel-style images so each card has a distinct visual.
const ALT_IMAGES = [
  "https://images.unsplash.com/photo-1488646953014-85cb44e25828?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1493558103817-58b2924bce98?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1530789253388-582c481c54b0?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1542051841857-5f90071e7989?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1518391846015-55a9cc003b25?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1539635278303-d4002c07eae3?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1533105079780-92b9be482077?crop=entropy&cs=srgb&fm=jpg&q=85",
];

const pickAltImage = (seed) => {
  const h = Math.abs(hashStr(seed));
  return ALT_IMAGES[h % ALT_IMAGES.length];
};
const pickAltImageOffset = (seed, offset) => {
  const h = Math.abs(hashStr(seed)) + offset * 7;
  return ALT_IMAGES[h % ALT_IMAGES.length];
};

function hashStr(s = "") {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0;
  }
  return h;
}

export const ReelInspiration = ({ spot, prompt = "" }) => {
  if (!spot) return null;

  const slug = (s = "") => s.replace(/[^A-Za-z0-9]+/g, "");
  const cards = buildCards(spot, prompt, slug);

  return (
    <section className="px-5 sm:px-8 pb-8">
      <div className="mx-auto max-w-5xl">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#FE0979] mb-1.5 inline-flex items-center gap-1.5">
              <Instagram className="h-3.5 w-3.5" /> Viral Reel Ideas
            </div>
            <h3 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">See what's already going viral. Then top it.</h3>
          </div>
        </div>

        <div className="snap-x-mandatory no-scrollbar overflow-x-auto flex gap-3 -mx-5 sm:-mx-8 px-5 sm:px-8 pb-2">
          {cards.map((c, i) => (
            <InspirationCard key={i} card={c} index={i} spotImage={spot.image} />
          ))}
        </div>

        <p className="mt-4 text-[11px] text-zinc-500 max-w-md">
          Tap any card to open Instagram Reels search and see what's trending. Use it as a moodboard before you shoot.
        </p>
      </div>
    </section>
  );
};

// Backwards-compat alias so existing imports keep working
export const TikTokInspiration = ReelInspiration;

function buildCards(spot, prompt, slug) {
  const city = spot.city || "";
  const country = spot.country || "";
  const tags = spot.vibe_tags || [];
  const promptText = (prompt || "").trim() || spot.name || city || country;
  const promptTitle = promptText.length > 40 ? `${promptText.slice(0, 40).trim()}…` : promptText;
  const promptTag = (slug(promptText.split(" ")[0]) || "travel").toLowerCase();
  const vibeTag = (slug(tags[0]) || "aesthetic").toLowerCase();

  const t1 = `${promptTag}reels`;
  const t2 = `${vibeTag}travel`;
  const t3 = `${promptTag}travel`;

  return [
    {
      label: "Prompt search",
      title: promptTitle,
      hashtag: `#${t1}`,
      sub: "Prompt search",
      query: promptText,
      icon: <Eye className="h-3 w-3" />,
      tone: "pink",
    },
    {
      label: tags[0] ? `${tags[0]} prompt` : "Aesthetic prompt",
      title: tags[0] || "Aesthetic shots",
      hashtag: `#${t2}`,
      sub: promptTitle,
      query: `${promptText} ${tags[0] || "aesthetic"}`.trim(),
      icon: <Music2 className="h-3 w-3" />,
      tone: "cyan",
    },
    {
      label: "Prompt moodboard",
      title: promptTitle,
      hashtag: `#${t3}`,
      sub: "Reels inspiration",
      query: `${promptText} reels`.trim(),
      icon: <Hash className="h-3 w-3" />,
      tone: "white",
    },
  ];
}

const InspirationCard = ({ card, index, spotImage }) => {
  // Use Instagram's tag URL — works on web (with optional IG login wall for some tags)
  // AND triggers the IG app via Universal Links on mobile. Real popular tags like
  // #balireels / #luxurytravel always have content and open without auth issues.
  const tag = (card.hashtag || "#travelreels").replace(/^#/, "").toLowerCase();
  const href = `https://www.instagram.com/explore/tags/${encodeURIComponent(tag)}/`;

  // Distinct image per card: card 0 uses the actual spot photo; cards 1 & 2 use
  // themed alternatives so the trio looks like a curated reel-style grid.
  const cardImage =
    index === 0
      ? (spotImage || pickAltImage(card.query))
      : pickAltImageOffset(card.query + card.hashtag, index);

  const toneRing = {
    pink: "ring-[#FE0979]/35",
    cyan: "ring-[#00F2FE]/30",
    white: "ring-white/15",
  }[card.tone];
  const toneBg = {
    pink: "bg-[#FE0979]",
    cyan: "bg-[#00F2FE]",
    white: "bg-white",
  }[card.tone];

  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      data-testid={`reel-inspo-${index}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: 0.06 * index, ease: [0.2, 0.7, 0.2, 1] }}
      className={`snap-center shrink-0 w-[62vw] sm:w-[230px] aspect-[9/16] rounded-3xl overflow-hidden relative ring-1 ${toneRing} hover-lift group`}
    >
      <img src={cardImage} alt={card.title} className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-[1.05]" />
      <div className="absolute inset-0 bg-black/35" />
      <div className="absolute inset-0 scrim-bottom" />

      <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
        <span className="glass rounded-full px-2 py-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em]">
          {card.icon} {card.label}
        </span>
        <span className={`h-7 w-7 rounded-full ${toneBg} text-black inline-flex items-center justify-center shrink-0`}>
          <Play className="h-3 w-3 ml-[1px]" fill="currentColor" />
        </span>
      </div>

      <div className="absolute inset-x-3 bottom-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-200/90 mb-1">
          {card.sub}
        </div>
        <div className="font-display font-black text-xl leading-tight text-shadow-soft mb-2 line-clamp-2">{card.title}</div>
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-white/95 truncate pr-2">{card.hashtag}</span>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white/90">
            Open Reels <ExternalLink className="h-3 w-3" />
          </span>
        </div>
      </div>

      {/* IG glyph corner */}
      <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition pointer-events-none">
        <Instagram className="h-3.5 w-3.5 text-white" />
      </div>
    </motion.a>
  );
};
